import React, { useState, useEffect } from "react";
import { LegalLayout, P, Strong, UL, LI, Callout, Table } from "./LegalLayout";

export default function PrivacyPolicy() {
  const [lang, setLang] = useState(() => localStorage.getItem("nexum-lang") || "es");
  useEffect(() => {
    localStorage.setItem("nexum-lang", lang);
    window.scrollTo(0, 0);
  }, [lang]);

  const content = {
    es: {
      title: "Política de Privacidad",
      subtitle:
        "En Nexum tratamos tus datos con el mismo rigor con el que diseñamos nuestro producto. Este documento explica qué recogemos, por qué, durante cuánto tiempo y qué derechos tienes sobre tu información.",
      lastUpdated: "Marzo 2026",
      sections: [
        {
          id: "responsable",
          heading: "Responsable del tratamiento",
          body: (
            <>
              <P>
                El responsable del tratamiento de los datos personales recabados a través de este sitio web y de los servicios de <Strong>Nexum</Strong> es:
              </P>
              <UL>
                <LI><Strong>Denominación social:</Strong> Nexum Business S.L.</LI>
                <LI><Strong>Domicilio:</Strong> Barcelona, España</LI>
                <LI><Strong>Correo electrónico:</Strong> hello@nexumbusiness.com</LI>
                <LI><Strong>Delegado de Protección de Datos (DPO):</Strong> dpo@nexumbusiness.com</LI>
              </UL>
              <P>
                Nos regimos por el Reglamento (UE) 2016/679 General de Protección de Datos (RGPD), por la Ley Orgánica 3/2018 de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD) y por la Ley 34/2002 de Servicios de la Sociedad de la Información y del Comercio Electrónico (LSSI-CE).
              </P>
            </>
          ),
        },
        {
          id: "datos",
          heading: "Datos que tratamos",
          body: (
            <>
              <P>Tratamos únicamente los datos estrictamente necesarios para prestar cada servicio. En función de tu relación con nosotros pueden incluir:</P>
              <UL>
                <LI><Strong>Datos de contacto e identificación:</Strong> nombre, apellidos, empresa, cargo, correo electrónico, teléfono.</LI>
                <LI><Strong>Datos de facturación:</Strong> razón social, NIF/CIF, dirección fiscal, información bancaria del cliente contratante.</LI>
                <LI><Strong>Datos de navegación:</Strong> dirección IP, tipo de dispositivo, sistema operativo, navegador, páginas visitadas, referrer, tiempo de sesión.</LI>
                <LI><Strong>Datos operativos del servicio:</Strong> transcripciones de llamadas, grabaciones de audio, mensajes de WhatsApp/chat, agendas y citas, cuando así lo requiera el servicio contratado.</LI>
                <LI><Strong>Datos derivados de comunicaciones comerciales:</Strong> preferencias, historial de contacto, respuestas a campañas.</LI>
              </UL>
              <Callout>
                No recabamos categorías especiales de datos (salud, ideología, orientación sexual, etc.) salvo consentimiento expreso y por escrito.
              </Callout>
            </>
          ),
        },
        {
          id: "finalidades",
          heading: "Finalidades y base jurídica",
          body: (
            <>
              <P>Tratamos tus datos únicamente para las finalidades legítimas que se detallan a continuación:</P>
              <Table
                headers={["Finalidad", "Base jurídica", "Conservación"]}
                rows={[
                  ["Prestación del servicio contratado", "Ejecución del contrato (art. 6.1.b RGPD)", "Duración del contrato + prescripción legal"],
                  ["Atender solicitudes de demo y contacto", "Consentimiento del interesado (art. 6.1.a RGPD)", "12 meses desde el último contacto"],
                  ["Facturación y obligaciones fiscales", "Obligación legal (art. 6.1.c RGPD)", "Mínimo 6 años (art. 30 CCom.)"],
                  ["Envío de comunicaciones comerciales", "Consentimiento o interés legítimo (art. 21 LSSI)", "Hasta revocación del consentimiento"],
                  ["Mejora del servicio y estadísticas", "Interés legítimo (art. 6.1.f RGPD)", "Máximo 24 meses"],
                  ["Cumplimiento de requerimientos legales", "Obligación legal (art. 6.1.c RGPD)", "El plazo exigido por la normativa aplicable"],
                ]}
              />
              <P>
                En ningún caso utilizaremos tus datos para finalidades incompatibles con las declaradas. Si en el futuro necesitáramos tratar tus datos para otra finalidad, te informaríamos previamente y, cuando sea legalmente exigible, solicitaríamos tu consentimiento.
              </P>
            </>
          ),
        },
        {
          id: "destinatarios",
          heading: "Destinatarios y encargados de tratamiento",
          body: (
            <>
              <P>
                No cedemos tus datos a terceros salvo obligación legal. Para prestar el servicio, sí trabajamos con proveedores tecnológicos cualificados que actúan como <Strong>encargados de tratamiento</Strong>, siempre bajo contrato conforme al artículo 28 RGPD:
              </P>
              <UL>
                <LI><Strong>Infraestructura y alojamiento:</Strong> proveedores cloud con centros de datos en la Unión Europea.</LI>
                <LI><Strong>Modelos de lenguaje e inteligencia artificial:</Strong> proveedores de LLM contratados con cláusulas de no entrenamiento y ubicación de datos en UE cuando resulta técnicamente posible.</LI>
                <LI><Strong>Telecomunicaciones:</Strong> operadores VoIP autorizados por la CNMC para la recepción y emisión de llamadas.</LI>
                <LI><Strong>Herramientas de productividad y CRM:</Strong> plataformas de calendario, correo y gestión de leads.</LI>
                <LI><Strong>Analítica y medición:</Strong> proveedores anonimizados y configurados con IP-truncation.</LI>
              </UL>
              <P>
                Puedes solicitar el detalle actualizado de nuestros encargados de tratamiento escribiendo a{" "}
                <a href="mailto:dpo@nexumbusiness.com" className="text-white underline underline-offset-4 hover:text-zinc-300">
                  dpo@nexumbusiness.com
                </a>.
              </P>
            </>
          ),
        },
        {
          id: "transferencias",
          heading: "Transferencias internacionales",
          body: (
            <>
              <P>
                Con carácter general, tus datos se almacenan y procesan dentro del Espacio Económico Europeo. Cuando algún proveedor esté ubicado fuera del EEE, garantizamos las salvaguardias exigidas por el Capítulo V del RGPD:
              </P>
              <UL>
                <LI>Decisiones de adecuación de la Comisión Europea, cuando existan.</LI>
                <LI>Cláusulas Contractuales Tipo (SCC) aprobadas por la Comisión Europea.</LI>
                <LI>Medidas técnicas suplementarias (cifrado en tránsito y en reposo, seudonimización).</LI>
              </UL>
            </>
          ),
        },
        {
          id: "derechos",
          heading: "Tus derechos",
          body: (
            <>
              <P>
                Como interesado dispones de los siguientes derechos, que puedes ejercer de forma gratuita en cualquier momento:
              </P>
              <UL>
                <LI><Strong>Acceso:</Strong> conocer qué datos tuyos tratamos y obtener una copia.</LI>
                <LI><Strong>Rectificación:</Strong> corregir datos inexactos o incompletos.</LI>
                <LI><Strong>Supresión (derecho al olvido):</Strong> solicitar la eliminación cuando ya no sean necesarios.</LI>
                <LI><Strong>Oposición:</Strong> oponerte a un tratamiento basado en interés legítimo.</LI>
                <LI><Strong>Limitación del tratamiento:</Strong> restringir el uso mientras se verifica una reclamación.</LI>
                <LI><Strong>Portabilidad:</Strong> recibir tus datos en formato estructurado y de uso común.</LI>
                <LI><Strong>Revocación del consentimiento:</Strong> en cualquier momento, sin efectos retroactivos.</LI>
                <LI><Strong>No ser objeto de decisiones automatizadas</Strong> que produzcan efectos jurídicos sobre ti.</LI>
              </UL>
              <P>
                Para ejercerlos, dirige tu solicitud a{" "}
                <a href="mailto:dpo@nexumbusiness.com" className="text-white underline underline-offset-4 hover:text-zinc-300">
                  dpo@nexumbusiness.com
                </a>{" "}
                indicando el derecho que deseas ejercer y adjuntando copia de tu documento de identidad. Responderemos en el plazo máximo de un mes.
              </P>
              <Callout>
                Si consideras que no hemos atendido tu solicitud debidamente, puedes presentar una reclamación ante la Agencia Española de Protección de Datos (aepd.es).
              </Callout>
            </>
          ),
        },
        {
          id: "seguridad",
          heading: "Medidas de seguridad",
          body: (
            <>
              <P>
                Aplicamos medidas técnicas y organizativas proporcionales al riesgo, alineadas con estándares reconocidos (ISO 27001, SOC 2 Type II en los proveedores críticos):
              </P>
              <UL>
                <LI>Cifrado TLS 1.3 en tránsito y AES-256 en reposo.</LI>
                <LI>Segregación de entornos y control de accesos por rol (RBAC).</LI>
                <LI>Autenticación multifactor obligatoria para personal con acceso a datos.</LI>
                <LI>Registro y auditoría de accesos.</LI>
                <LI>Planes de continuidad de negocio y recuperación ante desastres.</LI>
                <LI>Formación periódica del equipo en protección de datos y seguridad.</LI>
              </UL>
            </>
          ),
        },
        {
          id: "menores",
          heading: "Menores de edad",
          body: (
            <P>
              Nuestros servicios están dirigidos a profesionales y empresas. No recabamos conscientemente datos de menores de 14 años. Si detectáramos que hemos tratado datos de un menor sin el consentimiento parental exigido por la LOPDGDD, procederemos a su supresión inmediata.
            </P>
          ),
        },
        {
          id: "cambios",
          heading: "Cambios en la política",
          body: (
            <P>
              Podemos actualizar esta política para reflejar cambios normativos, técnicos o de negocio. Publicaremos la nueva versión en esta misma URL, actualizando la fecha de <em>Última actualización</em>. Cuando los cambios sean sustanciales, te avisaremos por un canal razonable (correo electrónico o aviso destacado en el sitio).
            </P>
          ),
        },
        {
          id: "autoridad",
          heading: "Autoridad de control",
          body: (
            <>
              <P>
                La autoridad de control competente en España es la <Strong>Agencia Española de Protección de Datos</Strong>:
              </P>
              <UL>
                <LI>C/ Jorge Juan, 6 · 28001 Madrid</LI>
                <LI>Sede electrónica: sedeagpd.gob.es</LI>
                <LI>Teléfono: 901 100 099 / 91 266 35 17</LI>
              </UL>
            </>
          ),
        },
      ],
    },
    en: {
      title: "Privacy Policy",
      subtitle:
        "At Nexum we treat your data with the same rigor we apply to our product. This document explains what we collect, why, for how long, and what rights you have over your information.",
      lastUpdated: "March 2026",
      sections: [
        {
          id: "responsable",
          heading: "Data controller",
          body: (
            <>
              <P>The controller of personal data collected through this website and Nexum services is:</P>
              <UL>
                <LI><Strong>Legal name:</Strong> Nexum Business S.L.</LI>
                <LI><Strong>Registered office:</Strong> Barcelona, Spain</LI>
                <LI><Strong>Email:</Strong> hello@nexumbusiness.com</LI>
                <LI><Strong>Data Protection Officer (DPO):</Strong> dpo@nexumbusiness.com</LI>
              </UL>
              <P>
                We comply with Regulation (EU) 2016/679 (GDPR), Spanish Organic Law 3/2018 (LOPDGDD) and Law 34/2002 on Information Society Services (LSSI-CE).
              </P>
            </>
          ),
        },
        {
          id: "datos",
          heading: "Data we process",
          body: (
            <>
              <P>We only process the data strictly necessary to deliver each service. Depending on your relationship with us this may include:</P>
              <UL>
                <LI><Strong>Contact and identification data:</Strong> name, surname, company, role, email, phone.</LI>
                <LI><Strong>Billing data:</Strong> legal name, tax ID, tax address, banking information of the contracting client.</LI>
                <LI><Strong>Browsing data:</Strong> IP address, device type, operating system, browser, pages visited, referrer, session time.</LI>
                <LI><Strong>Service operational data:</Strong> call transcripts, audio recordings, WhatsApp/chat messages, calendars and appointments, when required by the contracted service.</LI>
                <LI><Strong>Commercial communications data:</Strong> preferences, contact history, campaign responses.</LI>
              </UL>
              <Callout>
                We do not collect special categories of data (health, ideology, sexual orientation, etc.) unless explicit written consent is provided.
              </Callout>
            </>
          ),
        },
        {
          id: "finalidades",
          heading: "Purposes and legal basis",
          body: (
            <>
              <P>We process your data only for the legitimate purposes detailed below:</P>
              <Table
                headers={["Purpose", "Legal basis", "Retention"]}
                rows={[
                  ["Delivery of the contracted service", "Contract performance (art. 6.1.b GDPR)", "Contract term + legal statute of limitations"],
                  ["Handling demo and contact requests", "Consent (art. 6.1.a GDPR)", "12 months from last contact"],
                  ["Billing and tax obligations", "Legal obligation (art. 6.1.c GDPR)", "Min. 6 years (Spanish Commercial Code)"],
                  ["Commercial communications", "Consent or legitimate interest (art. 21 LSSI)", "Until consent is revoked"],
                  ["Service improvement and statistics", "Legitimate interest (art. 6.1.f GDPR)", "Max. 24 months"],
                  ["Legal compliance", "Legal obligation (art. 6.1.c GDPR)", "Term required by applicable law"],
                ]}
              />
              <P>
                We will never use your data for purposes incompatible with those declared. If further processing is required, we will inform you in advance and, when legally required, request your consent.
              </P>
            </>
          ),
        },
        {
          id: "destinatarios",
          heading: "Recipients and processors",
          body: (
            <>
              <P>
                We do not disclose your data to third parties except by legal obligation. To deliver the service, we work with qualified technology providers acting as <Strong>data processors</Strong>, always under a contract compliant with article 28 GDPR:
              </P>
              <UL>
                <LI><Strong>Infrastructure and hosting:</Strong> cloud providers with data centers in the European Union.</LI>
                <LI><Strong>LLMs and AI:</Strong> LLM providers contracted with no-training clauses and EU data residency where technically possible.</LI>
                <LI><Strong>Telecommunications:</Strong> VoIP operators authorized by the CNMC for call handling.</LI>
                <LI><Strong>Productivity and CRM tools:</Strong> calendar, email and lead management platforms.</LI>
                <LI><Strong>Analytics:</Strong> anonymized providers with IP truncation enabled.</LI>
              </UL>
              <P>
                You can request the up-to-date list of processors by writing to{" "}
                <a href="mailto:dpo@nexumbusiness.com" className="text-white underline underline-offset-4 hover:text-zinc-300">
                  dpo@nexumbusiness.com
                </a>.
              </P>
            </>
          ),
        },
        {
          id: "transferencias",
          heading: "International transfers",
          body: (
            <>
              <P>
                Generally, your data is stored and processed within the European Economic Area. When a provider is located outside the EEA, we guarantee the safeguards required by Chapter V of the GDPR:
              </P>
              <UL>
                <LI>European Commission adequacy decisions, where available.</LI>
                <LI>Standard Contractual Clauses (SCC) approved by the European Commission.</LI>
                <LI>Supplementary technical measures (encryption in transit and at rest, pseudonymization).</LI>
              </UL>
            </>
          ),
        },
        {
          id: "derechos",
          heading: "Your rights",
          body: (
            <>
              <P>As a data subject, you have the following rights, which you may exercise free of charge at any time:</P>
              <UL>
                <LI><Strong>Access:</Strong> to know what data we process and receive a copy.</LI>
                <LI><Strong>Rectification:</Strong> correct inaccurate or incomplete data.</LI>
                <LI><Strong>Erasure (right to be forgotten):</Strong> request deletion when no longer necessary.</LI>
                <LI><Strong>Objection:</Strong> object to processing based on legitimate interest.</LI>
                <LI><Strong>Restriction of processing:</Strong> restrict use while a claim is verified.</LI>
                <LI><Strong>Portability:</Strong> receive your data in a structured, commonly-used format.</LI>
                <LI><Strong>Withdrawal of consent:</Strong> at any time, without retroactive effect.</LI>
                <LI><Strong>Not to be subject to automated decisions</Strong> producing legal effects on you.</LI>
              </UL>
              <P>
                To exercise them, please write to{" "}
                <a href="mailto:dpo@nexumbusiness.com" className="text-white underline underline-offset-4 hover:text-zinc-300">
                  dpo@nexumbusiness.com
                </a>{" "}
                stating the right you wish to exercise and attaching a copy of your ID. We will respond within one month.
              </P>
              <Callout>
                If you believe we have not handled your request properly, you may file a complaint with the Spanish Data Protection Agency (aepd.es).
              </Callout>
            </>
          ),
        },
        {
          id: "seguridad",
          heading: "Security measures",
          body: (
            <>
              <P>We apply technical and organizational measures proportional to risk, aligned with recognized standards (ISO 27001, SOC 2 Type II on critical providers):</P>
              <UL>
                <LI>TLS 1.3 in transit and AES-256 at rest.</LI>
                <LI>Environment segregation and role-based access control (RBAC).</LI>
                <LI>Mandatory multi-factor authentication for staff with data access.</LI>
                <LI>Access logging and auditing.</LI>
                <LI>Business continuity and disaster recovery plans.</LI>
                <LI>Regular team training on data protection and security.</LI>
              </UL>
            </>
          ),
        },
        {
          id: "menores",
          heading: "Minors",
          body: (
            <P>
              Our services are intended for professionals and businesses. We do not knowingly collect data from minors under 14. If we detect that we have processed a minor's data without the parental consent required by LOPDGDD, we will delete it immediately.
            </P>
          ),
        },
        {
          id: "cambios",
          heading: "Policy changes",
          body: (
            <P>
              We may update this policy to reflect regulatory, technical or business changes. The new version will be published at this same URL and the <em>Last updated</em> date will change. When changes are material, we will notify you through a reasonable channel (email or prominent notice on the site).
            </P>
          ),
        },
        {
          id: "autoridad",
          heading: "Supervisory authority",
          body: (
            <>
              <P>The competent supervisory authority in Spain is the <Strong>Spanish Data Protection Agency</Strong>:</P>
              <UL>
                <LI>C/ Jorge Juan, 6 · 28001 Madrid</LI>
                <LI>Website: sedeagpd.gob.es</LI>
                <LI>Phone: +34 901 100 099 / +34 91 266 35 17</LI>
              </UL>
            </>
          ),
        },
      ],
    },
  };

  const c = content[lang];
  return (
    <LegalLayout
      lang={lang}
      setLang={setLang}
      meta={{ title: c.title, subtitle: c.subtitle, lastUpdated: c.lastUpdated }}
      sections={c.sections}
      related={[
        { href: "/terminos", label: lang === "es" ? "Términos de Uso" : "Terms of Use" },
        { href: "/cookies", label: lang === "es" ? "Política de Cookies" : "Cookie Policy" },
        { href: "/legal", label: lang === "es" ? "Aviso Legal" : "Legal Notice" },
      ]}
    />
  );
}
